<template>
  <v-dialog :value="isModalActive" max-width="420" @input="inputModal">
    <v-card
      id="headers-modal-card"
      class="pb-1 pr-2"
      :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
    >
      <v-card-text class="py-0 pt-5 pb-3">
        <div class="items-flex justify-between">
          <div class="card-title">
            {{ $t('views.common.headersModal.title') }}
          </div>

          <div>
            <v-tooltip top max-width="300px">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" size="18" v-on="on" @click="returnToDefaultHeaders">
                  mdi-reload
                </v-icon>
              </template>
              <span>{{ $t('views.common.headersModal.returnToDefaultHeaders') }}</span>
            </v-tooltip>

            <v-tooltip top max-width="300px">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-3" v-bind="attrs" size="18" v-on="on" @click="selectAllHeaders">
                  mdi-plus-box-multiple-outline
                </v-icon>
              </template>
              <span>{{ $t('views.common.headersModal.selectAllHeaders') }}</span>
            </v-tooltip>
          </div>
        </div>

        <div class="mt-5">
          <draggable v-model="myHeaders" handle=".handle" :animation="100" :move="checkMove">
            <transition-group>
              <div
                v-for="header in myHeaders"
                :key="header.value"
                class="headers-modal-card-header items-flex justify-between my-2"
                :class="{ hidden: header.customHidden }"
              >
                <div class="items-flex">
                  <div v-if="!header.customHideDisabled" class="mr-2">
                    <v-icon
                      v-if="header.customHidden"
                      size="17"
                      class="mb-1"
                      @click="$set(header, 'customHidden', false)"
                    >
                      mdi-plus-circle-outline
                    </v-icon>
                    <v-icon
                      v-else
                      size="17"
                      class="mb-1"
                      @click="$set(header, 'customHidden', true)"
                    >
                      mdi-check-circle
                    </v-icon>
                  </div>
                  <div class="d-flex" :class="{ hidden: header.customHidden }">
                    <div>
                      {{ header.customSecondaryText ? header.customSecondaryText : header.text }}
                    </div>
                    <div class="ml-2">
                      <InfoIcon
                        v-if="$te(`views.common.headersModal.info.${header.value}`)"
                        :size="13"
                        :text="$t(`views.common.headersModal.info.${header.value}`)"
                      />
                    </div>
                  </div>
                </div>

                <div :class="{ hidden: header.customHidden }" class="ml-1 handle">
                  <template v-if="!header.customDragDisabled">
                    <v-icon size="22">mdi-drag</v-icon>
                  </template>
                </div>
              </div>
            </transition-group>
          </draggable>
        </div>
      </v-card-text>

      <v-card-actions class="mt-0 pt-0">
        <v-spacer />
        <v-btn
          class="mr-1"
          text
          :disabled="loading"
          :small="$vuetify.breakpoint.smAndDown"
          @click="closeModal"
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="saveDisabled"
          :small="$vuetify.breakpoint.smAndDown"
          @click="save"
        >
          {{ $t('common.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { pick, get } from 'lodash'
import { mapState, mapActions } from 'vuex'
import { updateUser } from '@/lib/api.js'
import InfoIcon from '@/views/components/common/InfoIcon'
import modalMixin from '@/views/components/core/modalMixin.js'
import draggable from 'vuedraggable'

export default {
  components: { draggable, InfoIcon },
  mixins: [modalMixin],
  props: {
    tableName: {
      type: String,
      required: true,
    },
    defaultHeaders: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    myHeaders: [],
  }),
  computed: {
    ...mapState(['user']),
    userHeaders() {
      return get(this.user, `preferences.customTableHeaders.${this.tableName}`, [])
    },
    saveDisabled() {
      if (this.myHeaders.every((item) => item.customHidden)) return true
      return false
    },
  },
  watch: {
    userHeaders: {
      handler() {
        if (this.userHeaders && this.userHeaders.length)
          this.myHeaders = JSON.parse(JSON.stringify(this.userHeaders))
        else this.returnToDefaultHeaders()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions({ updateUsersInfo: 'UPDATE_USERS_INFO' }),
    resetForm() {
      this.myHeaders = []
    },
    returnToDefaultHeaders() {
      this.myHeaders = JSON.parse(JSON.stringify(this.defaultHeaders))
    },
    selectAllHeaders() {
      this.myHeaders = this.myHeaders.map((elem) => {
        delete elem.customHidden
        return elem
      })
    },
    checkMove(evt) {
      return !evt.relatedContext.element.customDragDisabled
    },
    async save() {
      this.loading = true
      try {
        const data = pick(this.user, ['preferences'])
        if (!data.preferences.customTableHeaders) data.preferences.customTableHeaders = {}
        data.preferences.customTableHeaders[this.tableName] = JSON.parse(
          JSON.stringify(this.myHeaders)
        )
        await updateUser({ uid: this.user.uid, data })
        await this.updateUsersInfo()
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        this.loading = false
      }
      this.loading = false

      this.closeModal()
    },
  },
}
</script>

<style lang="scss">
#headers-modal-card {
  .items-flex {
    display: flex;
    &.justify-between {
      justify-content: space-between;
    }
  }

  .headers-modal-card-header {
    border: 1px solid #eeeeee;
    border-radius: 5px;
    background-color: #f7f8fb;
    padding: 2px 7px 2px 10px;
    .handle {
      cursor: move; /* fallback if grab cursor is unsupported */
      cursor: grab;
      cursor: -moz-grab;
      cursor: -webkit-grab;
    }
    &.hidden {
      background-color: white;
    }
    .hidden {
      opacity: 0.5;
    }
    font-weight: 400;
    font-size: 13px;
  }
}

#headers-modal-card.theme--dark {
  .headers-modal-card-header {
    border: 1px solid #1e1e1e;
    background-color: #292929;
    &.hidden {
      background-color: #292929;
      opacity: 0.5;
    }
  }
}
</style>
